import { Paper, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import {
  FIELD_TYPE,
  useArableField,
  useArableFields,
  useField,
  useFields,
} from "model";
import { useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";

interface Props {
  fieldId?: string;
  dm: string;
  visible: boolean;
}

interface Position {
  x: number;
  y: number;
}

export const MapCursorTooltip = ({ fieldId, dm, visible }: Props) => {
  const { formatMessage: f } = useIntl();
  const [position, setPosition] = useState<Position>({ x: 0, y: 0 });

  // Get all fields to determine field type
  const { data: allFields = [] } = useFields();
  const { data: allArableFields = [] } = useArableFields();

  // Determine field type by checking which list contains the fieldId
  const fieldType = useMemo(() => {
    if (!fieldId) return undefined;
    const isGrassField = allFields.some((field) => field.guid === fieldId);
    if (isGrassField) return FIELD_TYPE.GRASS;
    const isArableField = allArableFields.some(
      (field) => field.guid === fieldId
    );
    if (isArableField) return FIELD_TYPE.ARABLE;
    return undefined;
  }, [fieldId, allFields, allArableFields]);

  // Use React Query to fetch field data - conditionally based on field type
  const { data: field } = useField(
    fieldType === FIELD_TYPE.GRASS ? fieldId : undefined
  );
  const { data: arableField } = useArableField(
    fieldType === FIELD_TYPE.ARABLE ? fieldId : undefined
  );

  const fieldName = field?.name ?? arableField?.name;

  const offset = { x: 15, y: 15 };

  useEffect(() => {
    const handleMouseChange = ({ clientX, clientY }: MouseEvent) => {
      setPosition({ x: clientX, y: clientY });
    };
    window.addEventListener("mousemove", handleMouseChange);
    return () => window.removeEventListener("mousemove", handleMouseChange);
  });

  return (
    <MapTooltip
      style={{
        display: visible ? "block" : "none",
        position: "fixed",
        top: position.y + offset.y,
        left: position.x + offset.x,
      }}
    >
      <Typography>{fieldName}</Typography>
      {dm != "-1" && (
        <Typography>
          {dm + " " + f({ id: "map.cursorTooltip.unit" })}
        </Typography>
      )}
    </MapTooltip>
  );
};

const MapTooltip = styled(Paper)(({ theme }) => ({
  textAlign: "center",
  padding: theme.spacing(1),
  zIndex: 10000,
}));
